'use strict'

/**
 * Full draft validator + editor-draft factories.
 *
 * Answers: "how do I author a well-formed draft (blank/edit/validate)?"
 *
 * Pure-fn validation of upstream-refining-contract drafts. No vuex / api /
 * dom dependencies — runs in isolation in tests, the editor component, and
 * any future programmatic caller (e.g. CSV import).
 *
 * Returns an array of human-readable error strings; empty array means valid.
 * Caller decides how to surface (inline form errors, toast, console).
 *
 * The shape mirrors intended live contract validation so the FE keeps
 * pre-flight checks aligned with a future persistence endpoint.
 *
 * Factory section: extracted so the create-blank vs edit-from-existing
 * branches are unit-testable without mounting Vue. Defaults keep the editor
 * compatible with both new contracts and legacy persisted contracts that
 * pre-date the `assayFee` / `otherFees` migration.
 */

import cloneDeep from 'lodash/cloneDeep'

import {
  ALLOWED_CURRENCY_CODES,
  ALLOWED_FEE_BASIS,
  ALLOWED_OTHER_FEE_CODES,
  ALLOWED_OTHER_FEE_KINDS,
  ALLOWED_PAID_METALS,
  ALLOWED_TIER_DIMENSIONS,
  DEFAULT_EFFECTIVE_TO,
} from './_shared'
import {
  normalizeReferenceFeeSchedule,
  resolveAssayFeeFromContract,
  resolveOtherFeesWithLegacy,
  validateImpurityChargesShape,
  validateMetalThresholdsShape,
  validateRefiningCostsShape,
} from './fees'
import {parseContractValidityDate} from './validity'

const LOT_WEIGHT_KINDS = ['minimum', 'maximum', 'guidance']
// Contract troy-ounce wire token: 'ozt'; no toz dual-accept.
const LOT_WEIGHT_UNITS = ['ozt', 'kg', 'metric_tonne']
const LOT_WEIGHT_ENFORCEMENTS = ['hard', 'advisory']
const LOT_WEIGHT_BASES = ['material_received', 'material_per_lot', 'shipment', 'settlement_lot', 'delivery']

// ============================================================================
// Full draft validator
// ============================================================================

export const validateContractDraft = (draft) => {
  if (!draft || typeof draft !== 'object') return ['draft required']
  const errors = []

  if (typeof draft.title !== 'string' || draft.title.trim() === '') {
    errors.push('title is required')
  }
  if (!draft.refiningPartnerId) {
    errors.push('refiningPartnerId is required')
  }

  const paidMetals = Array.isArray(draft.paidMetals) ? draft.paidMetals : []
  if (paidMetals.length === 0) {
    errors.push('at least one paid metal is required')
  }
  for (const metal of paidMetals) {
    if (!ALLOWED_PAID_METALS.includes(metal)) {
      errors.push(`paid metal "${metal}" is not allowed (allowed: ${ALLOWED_PAID_METALS.join(', ')})`)
    }
  }

  validatePrimaryMetals(draft.primaryMetals, paidMetals, errors)
  validateLotWeightRequirements(draft.lotWeightRequirements, errors)

  const tierDimension = draft.tierDimension === undefined || draft.tierDimension === null
    ? 'weightToz'
    : draft.tierDimension
  if (!ALLOWED_TIER_DIMENSIONS.includes(tierDimension)) {
    errors.push(`tierDimension must be one of: ${ALLOWED_TIER_DIMENSIONS.join(', ')}`)
  }

  const standardFees = draft.standardFees || {}
  const treatmentFeeUsd = standardFees.treatmentFeeUsd
  if (treatmentFeeUsd !== undefined && treatmentFeeUsd !== null) {
    if (!Number.isFinite(treatmentFeeUsd)) {
      errors.push('treatmentFeeUsd must be a finite number')
    } else if (treatmentFeeUsd < 0) {
      errors.push('treatmentFeeUsd must be ≥ 0')
    }
  }

  validateCurrency(draft, errors)
  validateMaterialAcceptanceCriteria(draft, errors)
  validateReferenceFeeScheduleField(draft, errors)
  validateSlaModifiers(draft, errors)
  validateTierQualifyingMetal(draft, paidMetals, tierDimension, errors)
  validateValidityWindow(draft, errors)
  validateAssayFee(draft, paidMetals, errors)
  validateOtherFees(draft, errors)

  const tiers = Array.isArray(draft.metalRates) ? draft.metalRates : []
  if (tiers.length === 0) {
    errors.push('at least one metal rate tier is required')
  }
  validateTierRows(tiers, paidMetals, tierDimension, errors)
  validateTierOverlap(tiers, errors)

  return errors
}


/** Join validation errors for API / store rejection messages. */
export const formatContractDraftErrors = (errors) => (Array.isArray(errors) ? errors : []).join('; ')

// ============================================================================
// Editor-draft factories (blank + project-from-existing)
// ============================================================================

/**
 * Pristine draft for the "new contract" flow. Empty paid metals + tier
 * tables, `tierDimension: weightToz` default, far-future `effectiveTo`
 * sentinel so the validator accepts the draft until an effective date is
 * chosen explicitly.
 */
export const buildEmptyContractDraft = () => ({
  title: '',
  refiningPartnerId: null,
  settlementTerms: '',
  tierDimension: 'weightToz',
  paidMetals: [],
  primaryMetals: [],
  lotWeightRequirements: [],
  metalRates: [],
  assayFee: {},
  otherFees: [],
  effectiveFrom: null,
  effectiveTo: DEFAULT_EFFECTIVE_TO,
  expectedMilestones: [],
  // Currency is a documentation tag — default USD; non-USD only
  // matters once admin opts in via the editor.
  currency: 'USD',
  // Pre-shipment intake gate text; null when no criteria.
  // MasterLotCreate surfaces an advisory banner once contract has
  // non-empty criteria.
  materialAcceptanceCriteria: null,
  // Reference-only fee schedule bag (Saxonia EUR refining costs,
  // Asahi multi-band impurity table). Default null; populated drafts
  // auto-expand the editor's subform.
  referenceFeeSchedule: null,
  // Conditional SLA "+N business days" modifiers (Saxonia Ag<70%
  // +10bd; Asahi-silver <200kg +10bd, hasImpurityCharges +10bd).
  // Default null.
  slaModifiers: null,
  // Cross-metal tier qualifier — re-routes purity-tier qualification
  // to a different metal (e.g. Asahi-silver routes Au+Ag rows through
  // Ag fineness). Null = standard per-row own-metal qualification.
  tierQualifyingMetal: null,
})

/**
 * Project an existing contract into the editor's draft shape. Falls back
 * to defaults for any missing / malformed fields so legacy contracts (no
 * `assayFee` map, flat `treatmentFeeUsd` only, etc.) can be opened
 * without forcing the operator to re-key every field.
 *
 * Legacy fan-outs handled:
 *   - `assayFee`: via `resolveAssayFeeFromContract` (falls back to
 *     `standardFees.assayFeeUsd` divided across paidMetals)
 *   - `otherFees`: when missing but `standardFees.treatmentFeeUsd > 0`,
 *     synthesize a single `{code: 'treatment', kind: 'flat', flatUsd}` row
 */
export const buildContractDraftFrom = (contract) => {
  if (!contract) return buildEmptyContractDraft()
  const cloned = cloneDeep(contract)
  return {
    title: cloned.title || '',
    refiningPartnerId: cloned.refiningPartnerId || null,
    settlementTerms: cloned.settlementTerms || '',
    tierDimension: ALLOWED_TIER_DIMENSIONS.includes(cloned.tierDimension) ? cloned.tierDimension : 'weightToz',
    paidMetals: Array.isArray(cloned.paidMetals) ? cloned.paidMetals : [],
    primaryMetals: Array.isArray(cloned.primaryMetals) ? cloned.primaryMetals : [],
    lotWeightRequirements: Array.isArray(cloned.lotWeightRequirements) ? cloned.lotWeightRequirements : [],
    metalRates: Array.isArray(cloned.metalRates) ? cloned.metalRates : [],
    assayFee: resolveAssayFeeFromContract(cloned),
    otherFees: resolveOtherFeesWithLegacy(cloned),
    effectiveFrom: cloned.effectiveFrom || null,
    effectiveTo: cloned.effectiveTo || DEFAULT_EFFECTIVE_TO,
    expectedMilestones: Array.isArray(cloned.expectedMilestones) ? cloned.expectedMilestones : [],
    currency: ALLOWED_CURRENCY_CODES.includes(cloned.currency) ? cloned.currency : 'USD',
    materialAcceptanceCriteria: typeof cloned.materialAcceptanceCriteria === 'string'
      ? cloned.materialAcceptanceCriteria
      : null,
    referenceFeeSchedule: normalizeReferenceFeeSchedule(cloned.referenceFeeSchedule),
    slaModifiers: normalizeSlaModifiers(cloned.slaModifiers),
    tierQualifyingMetal: ALLOWED_PAID_METALS.includes(cloned.tierQualifyingMetal)
      ? cloned.tierQualifyingMetal
      : null,
  }
}

// ============================================================================
// Validation helpers
// ============================================================================

// 'tierQualifyingMetal' — single contract-wide enum re-routing purity-tier qualification to a different metal (Asahi-silver: Au+Ag on Ag; Asahi-gold: Ag+Pt+Pd on Au). Valid only when 'tierDimension === purityPct' and the metal is in 'paidMetals'. Null is standard own-metal qualification.
const validateTierQualifyingMetal = (draft, paidMetals, tierDimension, errors) => {
  const raw = draft.tierQualifyingMetal
  if (raw === undefined || raw === null) return
  if (typeof raw !== 'string' || !ALLOWED_PAID_METALS.includes(raw)) {
    errors.push(`tierQualifyingMetal must be one of: ${ALLOWED_PAID_METALS.join(', ')}`)
    return
  }
  if (paidMetals.length > 0 && !paidMetals.includes(raw)) {
    errors.push(`tierQualifyingMetal "${raw}" must be in paidMetals`)
  }
  if (tierDimension !== 'purityPct') {
    errors.push('tierQualifyingMetal requires tierDimension === "purityPct"')
  }
}

const validatePrimaryMetals = (primaryMetals, paidMetals, errors) => {
  if (!Array.isArray(primaryMetals) || primaryMetals.length === 0) {
    errors.push('at least one primary metal is required')
    return
  }
  if (new Set(primaryMetals).size !== primaryMetals.length) {
    errors.push('primary metals must be unique')
  }
  primaryMetals.forEach((metal) => {
    if (!ALLOWED_PAID_METALS.includes(metal)) {
      errors.push(`primary metal "${metal}" is not allowed (allowed: ${ALLOWED_PAID_METALS.join(', ')})`)
    } else if (!paidMetals.includes(metal)) {
      errors.push(`primary metal "${metal}" must be in paidMetals`)
    }
  })
}

const validateLotWeightRequirements = (requirements, errors) => {
  if (requirements === undefined || requirements === null) return
  if (!Array.isArray(requirements)) {
    errors.push('lotWeightRequirements must be an array')
    return
  }
  const seenKinds = new Set()
  requirements.forEach((requirement, index) => {
    const ordinal = index + 1
    if (!requirement || typeof requirement !== 'object') {
      errors.push(`lotWeightRequirements[${ordinal}] must be an object`)
      return
    }
    if (!LOT_WEIGHT_KINDS.includes(requirement.kind)) {
      errors.push(`lotWeightRequirements[${ordinal}] kind must be one of: ${LOT_WEIGHT_KINDS.join(', ')}`)
    }
    if (!Number.isFinite(requirement.value) || requirement.value <= 0) {
      errors.push(`lotWeightRequirements[${ordinal}] value must be > 0`)
    }
    if (!LOT_WEIGHT_UNITS.includes(requirement.unit)) {
      errors.push(`lotWeightRequirements[${ordinal}] unit must be one of: ${LOT_WEIGHT_UNITS.join(', ')}`)
    }
    if (typeof requirement.basis !== 'string' || requirement.basis.trim() === '') {
      errors.push(`lotWeightRequirements[${ordinal}] basis is required`)
    } else if (!LOT_WEIGHT_BASES.includes(requirement.basis)) {
      errors.push(`lotWeightRequirements[${ordinal}] basis must be one of: ${LOT_WEIGHT_BASES.join(', ')}`)
    } else {
      const identity = `${requirement.kind}:${requirement.basis}`
      if (seenKinds.has(identity)) {
        errors.push(`lotWeightRequirements kind "${requirement.kind}" must be unique per basis`)
      } else {
        seenKinds.add(identity)
      }
    }
    if (!LOT_WEIGHT_ENFORCEMENTS.includes(requirement.enforcement)) {
      errors.push(`lotWeightRequirements[${ordinal}] enforcement must be one of: ${LOT_WEIGHT_ENFORCEMENTS.join(', ')}`)
    }
  })
}

const validateTierRows = (tiers, paidMetals, tierDimension, errors) => {
  const isPurityScale = tierDimension === 'purityPct'
  tiers.forEach((row, idx) => {
    const ordinal = idx + 1
    const rowMetal = row && row.metalType
    if (rowMetal && paidMetals.length > 0 && !paidMetals.includes(rowMetal)) {
      errors.push(`tier ${ordinal} metal "${rowMetal}" is not in paidMetals`)
    }
    const rowQualifier = row && row.tierQualifyingMetal
    if (rowQualifier !== undefined && rowQualifier !== null) {
      if (!ALLOWED_PAID_METALS.includes(rowQualifier)) {
        errors.push(`tier ${ordinal} tierQualifyingMetal must be one of: ${ALLOWED_PAID_METALS.join(', ')}`)
      } else if (!paidMetals.includes(rowQualifier)) {
        errors.push(`tier ${ordinal} tierQualifyingMetal "${rowQualifier}" must be in paidMetals`)
      } else if (tierDimension !== 'purityPct') {
        errors.push(`tier ${ordinal} tierQualifyingMetal requires tierDimension === "purityPct"`)
      }
    }

    const tierMin = row && row.tierMin
    const tierMax = row && row.tierMax

    if (!Number.isFinite(tierMin)) {
      errors.push(`tier ${ordinal} tierMin must be a finite number`)
    } else if (tierMin < 0) {
      errors.push(`tier ${ordinal} tierMin must be ≥ 0`)
    } else if (isPurityScale && tierMin > 100) {
      errors.push(`tier ${ordinal} tierMin must be in [0, 100] when tierDimension is purityPct`)
    }
    // tierMax of null / undefined means "unbounded" — only validate when present.
    if (tierMax !== null && tierMax !== undefined) {
      if (!Number.isFinite(tierMax)) {
        errors.push(`tier ${ordinal} tierMax must be a finite number or null`)
      } else if (Number.isFinite(tierMin) && tierMax <= tierMin) {
        errors.push(`tier ${ordinal} tierMax must be greater than tierMin`)
      } else if (isPurityScale && tierMax > 100) {
        errors.push(`tier ${ordinal} tierMax must be in [0, 100] when tierDimension is purityPct`)
      }
    }

    const acct = row && row.accountabilityPercent
    if (!Number.isFinite(acct)) {
      errors.push(`tier ${ordinal} accountabilityPercent must be a finite number`)
    } else if (acct < 0 || acct > 100) {
      errors.push(`tier ${ordinal} accountabilityPercent must be in [0, 100]`)
    }

    const fee = row && row.fee
    if (!Number.isFinite(fee)) {
      errors.push(`tier ${ordinal} fee must be a finite number`)
    } else if (fee < 0) {
      errors.push(`tier ${ordinal} fee must be ≥ 0`)
    }

    const minimumReturnToz = row && row.minimumReturnToz
    if (minimumReturnToz !== null && minimumReturnToz !== undefined) {
      if (!Number.isFinite(minimumReturnToz)) {
        errors.push(`tier ${ordinal} minimumReturnToz must be a finite number or null`)
      } else if (minimumReturnToz < 0) {
        errors.push(`tier ${ordinal} minimumReturnToz must be ≥ 0`)
      }
    }

    const settlementBusinessDays = row && row.settlementBusinessDays
    if (settlementBusinessDays !== null && settlementBusinessDays !== undefined) {
      if (!Number.isFinite(settlementBusinessDays)) {
        errors.push(`tier ${ordinal} settlementBusinessDays must be a finite number or null`)
      } else if (settlementBusinessDays < 0) {
        errors.push(`tier ${ordinal} settlementBusinessDays must be ≥ 0`)
      } else if (settlementBusinessDays > 365) {
        errors.push(`tier ${ordinal} settlementBusinessDays must be ≤ 365`)
      }
    }

    // Per-row notes — operator-typed freeform string for partner reconciliation. No length cap; null and absent both mean no note (no compute effect).
    const notes = row && row.notes
    if (notes !== undefined && notes !== null && typeof notes !== 'string') {
      errors.push(`tier ${ordinal} notes must be a string or null`)
    }

    // Per-tier 'feeBasis' enum. Missing silently defaults to 'creditedFineToz' at math (forward-compat). Empty/whitespace-only strings are hard errors (ambiguous intent — operator likely meant to clear it).
    const feeBasis = row && row.feeBasis
    if (feeBasis !== undefined && feeBasis !== null) {
      if (typeof feeBasis !== 'string' || feeBasis.trim().length === 0) {
        errors.push(`tier ${ordinal} feeBasis must be a non-empty string from: ${ALLOWED_FEE_BASIS.join(', ')}`)
      } else if (!ALLOWED_FEE_BASIS.includes(feeBasis)) {
        errors.push(`tier ${ordinal} feeBasis "${feeBasis}" must be one of: ${ALLOWED_FEE_BASIS.join(', ')}`)
      }
    }
  })
}

// Half-open '[tierMin, tierMax)' overlap: ranges overlap iff 'next.min < prior.max'. Touching boundaries ('next.min === prior.max') are adjacency, NOT overlap; canonical '[0, 20000)' + '[20000, null]' validates clean. Gaps are not errors (legacy fixtures may have intentional gaps).
const validateTierOverlap = (tiers, errors) => {
  const byMetal = {}
  for (const row of tiers) {
    if (!row || !row.metalType) continue
    if (!Number.isFinite(row.tierMin)) continue
    const max = row.tierMax === null || row.tierMax === undefined ? Infinity : row.tierMax
    if (!Number.isFinite(max) && max !== Infinity) continue
    if (!byMetal[row.metalType]) byMetal[row.metalType] = []
    byMetal[row.metalType].push({min: row.tierMin, max})
  }
  for (const [metal, ranges] of Object.entries(byMetal)) {
    const sorted = [...ranges].sort((a, b) => a.min - b.min)
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i].min < sorted[i - 1].max) {
        errors.push(`${metal} tiers overlap (range starting at ${sorted[i].min} conflicts with prior tier ending at ${sorted[i - 1].max})`)
      }
    }
  }
}

// 'currency' is a documentation tag (USD default, extendable enum); missing falls back to USD. 'materialAcceptanceCriteria' is a freeform multiline note — no length cap; empty/whitespace stays valid (editor normalizes trimmed-empty to null on save).
const validateMaterialAcceptanceCriteria = (draft, errors) => {
  const raw = draft.materialAcceptanceCriteria
  if (raw === undefined || raw === null) return
  if (typeof raw !== 'string') {
    errors.push('materialAcceptanceCriteria must be a string or null')
  }
}

const validateCurrency = (draft, errors) => {
  const raw = draft.currency
  if (raw === undefined || raw === null) return
  if (typeof raw !== 'string' || !ALLOWED_CURRENCY_CODES.includes(raw)) {
    errors.push(`currency must be one of: ${ALLOWED_CURRENCY_CODES.join(', ')}`)
  }
}

// 'referenceFeeSchedule' is a typed reference-only fee catalogue (Saxonia EUR costs, Asahi multi-band impurity charges). Never read by compute. Null/absent both mean no schedule; editor normalizes empty-object to null on save.
const validateReferenceFeeScheduleField = (draft, errors) => {
  const raw = draft.referenceFeeSchedule
  if (raw === undefined || raw === null) return
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    errors.push('referenceFeeSchedule must be an object or null')
    return
  }
  const paidMetals = Array.isArray(draft.paidMetals) ? draft.paidMetals : []
  errors.push(...validateImpurityChargesShape(raw.impurityCharges))
  errors.push(...validateRefiningCostsShape(raw.refiningCosts, paidMetals, ALLOWED_CURRENCY_CODES))
  errors.push(...validateMetalThresholdsShape(raw.metalThresholds, paidMetals))
  if (raw.perLotMinimumFee !== null && raw.perLotMinimumFee !== undefined) {
    if (!Number.isFinite(raw.perLotMinimumFee) || raw.perLotMinimumFee < 0) {
      errors.push('referenceFeeSchedule.perLotMinimumFee must be ≥ 0')
    }
  }
  if (raw.additionalNotes !== null && raw.additionalNotes !== undefined && typeof raw.additionalNotes !== 'string') {
    errors.push('referenceFeeSchedule.additionalNotes must be a string or null')
  }
}

// 'slaModifiers' is a list of conditional '+N business days' extensions layered on per-tier 'settlementBusinessDays'. ALL populated 'when' keys must match (AND semantics within one modifier; OR semantics come from declaring multiple). NOTE: the settlement-side 'adHocFees[].code' enum lives in upstreamMasterLot.js — different domain object despite shared labels.
const validateSlaModifiers = (draft, errors) => {
  const raw = draft.slaModifiers
  if (raw === undefined || raw === null) return
  if (!Array.isArray(raw)) {
    errors.push('slaModifiers must be an array or null')
    return
  }
  const paidMetals = Array.isArray(draft.paidMetals) ? draft.paidMetals : []
  const seenWhenShapes = new Set()
  raw.forEach((mod, idx) => {
    const ordinal = idx + 1
    if (!mod || typeof mod !== 'object') {
      errors.push(`slaModifiers[${ordinal}] must be an object`)
      return
    }
    if (!Number.isFinite(mod.addBusinessDays)) {
      errors.push(`slaModifiers[${ordinal}] addBusinessDays must be a finite number`)
    } else if (mod.addBusinessDays < -365 || mod.addBusinessDays > 365) {
      errors.push(`slaModifiers[${ordinal}] addBusinessDays must be in [-365, 365]`)
    }
    if (mod.notes !== undefined && mod.notes !== null && typeof mod.notes !== 'string') {
      errors.push(`slaModifiers[${ordinal}] notes must be a string or null`)
    }
    const when = mod.when
    if (!when || typeof when !== 'object') {
      errors.push(`slaModifiers[${ordinal}] when must be an object with at least one condition`)
      return
    }
    const allowedKeys = ['metal', 'purityPctMax', 'purityPctMin', 'lotWeightKgMax', 'lotWeightTozMax', 'hasImpurityCharges']
    const populatedKeys = allowedKeys.filter((k) => when[k] !== undefined && when[k] !== null)
    if (populatedKeys.length === 0) {
      errors.push(`slaModifiers[${ordinal}] when must populate at least one condition`)
    }
    for (const key of Object.keys(when)) {
      if (!allowedKeys.includes(key) && when[key] !== undefined && when[key] !== null) {
        errors.push(`slaModifiers[${ordinal}] when.${key} is not a recognized condition`)
      }
    }
    if (when.metal !== undefined && when.metal !== null) {
      if (!ALLOWED_PAID_METALS.includes(when.metal)) {
        errors.push(`slaModifiers[${ordinal}] when.metal must be one of: ${ALLOWED_PAID_METALS.join(', ')}`)
      } else if (paidMetals.length > 0 && !paidMetals.includes(when.metal)) {
        errors.push(`slaModifiers[${ordinal}] when.metal "${when.metal}" must be in paidMetals`)
      }
    }
    const purityRequiresMetal = (when.purityPctMax !== undefined && when.purityPctMax !== null)
      || (when.purityPctMin !== undefined && when.purityPctMin !== null)
    if (purityRequiresMetal && (when.metal === undefined || when.metal === null)) {
      errors.push(`slaModifiers[${ordinal}] when.metal is required when purityPctMin/Max is set`)
    }
    for (const key of ['purityPctMin', 'purityPctMax']) {
      const value = when[key]
      if (value === undefined || value === null) continue
      if (!Number.isFinite(value) || value < 0 || value > 100) {
        errors.push(`slaModifiers[${ordinal}] when.${key} must be in [0, 100]`)
      }
    }
    if (
      when.purityPctMin !== undefined && when.purityPctMin !== null
      && when.purityPctMax !== undefined && when.purityPctMax !== null
      && Number.isFinite(when.purityPctMin) && Number.isFinite(when.purityPctMax)
      && when.purityPctMin > when.purityPctMax
    ) {
      errors.push(`slaModifiers[${ordinal}] when.purityPctMin must be ≤ when.purityPctMax`)
    }
    const lotKg = when.lotWeightKgMax
    const lotToz = when.lotWeightTozMax
    if (lotKg !== undefined && lotKg !== null && (!Number.isFinite(lotKg) || lotKg <= 0)) {
      errors.push(`slaModifiers[${ordinal}] when.lotWeightKgMax must be > 0`)
    }
    if (lotToz !== undefined && lotToz !== null && (!Number.isFinite(lotToz) || lotToz <= 0)) {
      errors.push(`slaModifiers[${ordinal}] when.lotWeightTozMax must be > 0`)
    }
    if (
      lotKg !== undefined && lotKg !== null
      && lotToz !== undefined && lotToz !== null
    ) {
      errors.push(`slaModifiers[${ordinal}] when.lotWeightKgMax and when.lotWeightTozMax are mutually exclusive`)
    }
    if (when.hasImpurityCharges !== undefined && when.hasImpurityCharges !== null && when.hasImpurityCharges !== true) {
      errors.push(`slaModifiers[${ordinal}] when.hasImpurityCharges must be true (or omitted)`)
    }
    // Duplicate-detection: serialize the populated when subset deterministically.
    const stableWhen = JSON.stringify(populatedKeys.sort().map((k) => [k, when[k]]))
    if (seenWhenShapes.has(stableWhen)) {
      errors.push(`slaModifiers[${ordinal}] when shape duplicates an earlier modifier`)
    } else {
      seenWhenShapes.add(stableWhen)
    }
  })
}

const validateValidityWindow = (draft, errors) => {
  const effectiveToRaw = draft.effectiveTo
  if (typeof effectiveToRaw !== 'string' || effectiveToRaw.trim() === '') {
    errors.push('effectiveTo is required')
    return
  }
  const effectiveTo = parseContractValidityDate(effectiveToRaw)
  if (!effectiveTo) {
    errors.push('effectiveTo must be a valid yyyy-mm-dd date')
    return
  }
  const fromRaw = draft.effectiveFrom
  if (fromRaw === null || fromRaw === undefined || fromRaw === '') return
  if (typeof fromRaw !== 'string') {
    errors.push('effectiveFrom must be a yyyy-mm-dd date string or null')
    return
  }
  const effectiveFrom = parseContractValidityDate(fromRaw)
  if (!effectiveFrom) {
    errors.push('effectiveFrom must be a valid yyyy-mm-dd date')
    return
  }
  if (effectiveFrom.getTime() > effectiveTo.getTime()) {
    errors.push('effectiveFrom must be on or before effectiveTo')
  }
}

const validateOtherFees = (draft, errors) => {
  const rows = Array.isArray(draft.otherFees) ? draft.otherFees : []
  const seenCodes = new Set()
  rows.forEach((row, idx) => {
    const ordinal = idx + 1
    const code = row && row.code
    if (code && seenCodes.has(code)) {
      errors.push(`duplicate otherFees code "${code}"`)
    } else if (code) {
      seenCodes.add(code)
    }
    if (!ALLOWED_OTHER_FEE_CODES.includes(code)) {
      errors.push(`otherFees[${ordinal}] code must be one of: ${ALLOWED_OTHER_FEE_CODES.join(', ')}`)
    }
    if (!ALLOWED_OTHER_FEE_KINDS.includes(row && row.kind)) {
      errors.push(`otherFees[${ordinal}] kind must be one of: ${ALLOWED_OTHER_FEE_KINDS.join(', ')}`)
    }
    if (row && (row.kind === 'flat' || row.kind === 'flatPerEvent')) {
      const flatUsd = row.flatUsd
      if (!Number.isFinite(flatUsd) || flatUsd < 0) {
        errors.push(`otherFees[${ordinal}] flatUsd must be ≥ 0`)
      }
    }
    if (row && row.kind === 'perToz') {
      // Rows with populated 'purityTiers' short-circuit the flat 'perTozUsd' requirement (band rates carry the per-toz value). Flat 'perTozUsd' still accepted as a round-trip sentinel.
      const hasTiers = Array.isArray(row.purityTiers) && row.purityTiers.length > 0
      if (!hasTiers) {
        if (!Number.isFinite(row.perTozUsd) || row.perTozUsd < 0) {
          errors.push(`otherFees[${ordinal}] perTozUsd must be ≥ 0`)
        }
        if (row.minimumUsd !== null && row.minimumUsd !== undefined) {
          if (!Number.isFinite(row.minimumUsd) || row.minimumUsd < 0) {
            errors.push(`otherFees[${ordinal}] minimumUsd must be ≥ 0`)
          }
        }
      }
      validatePurityTiers(row.purityTiers, ordinal, draft, errors)
    } else if (row && Array.isArray(row.purityTiers) && row.purityTiers.length > 0) {
      errors.push(`otherFees[${ordinal}] purityTiers is only valid on kind: 'perToz' rows`)
    }
    // Per-row notes — same shape as metalRates notes; null and absent both mean no note.
    const notes = row && row.notes
    if (notes !== undefined && notes !== null && typeof notes !== 'string') {
      errors.push(`otherFees[${ordinal}] notes must be a string or null`)
    }
  })
}

// Purity-tiered 'otherFees': bands must be adjacent (no gap, no overlap) under half-open '[minPct, maxPct)'; first 'minPct' ≥ 0; last 'maxPct' may be 'null'. Per-band 'perTozUsd' ≥ 0; optional 'minimumUsd' ≥ 0. Hard requirement: 'contract.tierQualifyingMetal' must be set (qualifying-purity source is otherwise ambiguous).
const validatePurityTiers = (purityTiers, ordinal, draft, errors) => {
  if (purityTiers === undefined || purityTiers === null) return
  if (!Array.isArray(purityTiers)) {
    errors.push(`otherFees[${ordinal}] purityTiers must be an array or null`)
    return
  }
  if (purityTiers.length === 0) return
  if (!draft.tierQualifyingMetal) {
    errors.push(`otherFees[${ordinal}] purityTiers requires contract.tierQualifyingMetal to be set`)
  }
  let priorMax = null
  purityTiers.forEach((band, bandIdx) => {
    const bandOrdinal = bandIdx + 1
    if (!band || typeof band !== 'object') {
      errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] must be an object`)
      return
    }
    const minPct = band.minPct
    const maxPct = band.maxPct
    if (!Number.isFinite(minPct)) {
      errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] minPct must be a finite number`)
    } else if (bandIdx === 0 && minPct < 0) {
      errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] minPct must be ≥ 0`)
    } else if (minPct > 100) {
      errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] minPct must be in [0, 100]`)
    }
    if (maxPct !== null && maxPct !== undefined) {
      if (!Number.isFinite(maxPct)) {
        errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] maxPct must be a finite number or null`)
      } else if (Number.isFinite(minPct) && maxPct <= minPct) {
        errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] maxPct must be greater than minPct`)
      } else if (maxPct > 100) {
        errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] maxPct must be in [0, 100]`)
      }
    }
    if (!Number.isFinite(band.perTozUsd) || band.perTozUsd < 0) {
      errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] perTozUsd must be ≥ 0`)
    }
    if (band.minimumUsd !== undefined && band.minimumUsd !== null) {
      if (!Number.isFinite(band.minimumUsd) || band.minimumUsd < 0) {
        errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] minimumUsd must be ≥ 0`)
      }
    }
    if (priorMax !== null && Number.isFinite(minPct) && minPct !== priorMax) {
      errors.push(`otherFees[${ordinal}] purityTiers[${bandOrdinal}] minPct must equal prior band's maxPct for adjacency without gap`)
    }
    priorMax = (maxPct === null || maxPct === undefined) ? null : maxPct
  })
}

// Validate the raw draft's `assayFee` map directly. Missing rows or
// `feeUsd: 0` are accepted as "no fee charged" so the editor's pre-fill
// defaults pass through; only explicit non-finite or negative values fail.
const validateAssayFee = (draft, paidMetals, errors) => {
  const source = (draft.assayFee && typeof draft.assayFee === 'object') ? draft.assayFee : {}
  for (const metal of paidMetals) {
    const key = String(metal).toLowerCase()
    const row = source[key]
    if (row === undefined || row === null) continue
    if (row.feeUsd === undefined || row.feeUsd === null) continue
    if (!Number.isFinite(row.feeUsd)) {
      errors.push(`assayFee.${key}.feeUsd must be a finite number`)
    } else if (row.feeUsd < 0) {
      errors.push(`assayFee.${key}.feeUsd must be ≥ 0`)
    }
    if (row.purityTiers !== undefined && row.purityTiers !== null) {
      validateAssayFeePurityTiers(row, key, draft, errors)
    }
  }
}

const validateAssayFeePurityTiers = (row, metal, draft, errors) => {
  if (!Array.isArray(row.purityTiers) || row.purityTiers.length === 0) {
    errors.push(`assayFee.${metal}.purityTiers must be a non-empty array`)
    return
  }
  const qualifier = row.tierQualifyingMetal || metal
  if (!ALLOWED_PAID_METALS.includes(qualifier) || !draft.paidMetals.includes(qualifier)) {
    errors.push(`assayFee.${metal}.tierQualifyingMetal must be in paidMetals`)
  }
  let priorMax = null
  let unboundedTierSeen = false
  row.purityTiers.forEach((tier, index) => {
    const ordinal = index + 1
    if (unboundedTierSeen) {
      errors.push(`assayFee.${metal}.purityTiers[${ordinal}] no tiers may follow an unbounded band`)
    }
    if (!tier || !Number.isFinite(tier.minPct) || tier.minPct < 0 || tier.minPct > 100) {
      errors.push(`assayFee.${metal}.purityTiers[${ordinal}] minPct must be in [0, 100]`)
      return
    }
    if (tier.maxPct !== null && tier.maxPct !== undefined) {
      if (!Number.isFinite(tier.maxPct) || tier.maxPct <= tier.minPct || tier.maxPct > 100) {
        errors.push(`assayFee.${metal}.purityTiers[${ordinal}] maxPct must be greater than minPct and ≤ 100`)
      }
    }
    if (!Number.isFinite(tier.feeUsd) || tier.feeUsd < 0) {
      errors.push(`assayFee.${metal}.purityTiers[${ordinal}] feeUsd must be ≥ 0`)
    }
    if (priorMax !== null && tier.minPct !== priorMax) {
      errors.push(`assayFee.${metal}.purityTiers[${ordinal}] minPct must equal prior band's maxPct`)
    }
    priorMax = tier.maxPct === null || tier.maxPct === undefined ? null : tier.maxPct
    if (priorMax === null) unboundedTierSeen = true
  })
}

// ============================================================================
// Factory helpers
// ============================================================================

const normalizeSlaModifiers = (raw) => {
  if (!Array.isArray(raw)) return null
  const cleaned = raw.filter((mod) => mod && typeof mod === 'object' && mod.when)
  return cleaned.length > 0 ? cleaned : null
}
